import { UseFormMethods, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import {
    AGENTS_PERM_NAME,
    CXR_SUBSCRIPTION_NAME,
    CXS_SUBSCRIPTION_NAME,
    DEVICES_PERM_NAME,
    ENDPOINT_AGENT_NAME,
    ENDPOINT_DEVICE_NAME,
    REPORTING_LEVEL_NAME
} from '../FieldNames'
import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import {
    ENDPOINT_FIELD_KIND,
    EndpointFieldKind,
    SERVER_ALL_VALUE,
    SERVER_NONE_VALUE,
    SERVER_UNKNOWN_VALUE
} from '../Constants'
import { CXScoreLicences } from '../../../NonReact/constants/CXScoreLicences'
import { CXRecordLicences } from '../../../NonReact/constants/CXRecordLicences'
import { CXRecordLanguage, CXScoreLanguage } from '../../../../i18n'
import { CX_RECORD_LICENCE_I18N_PREFIX } from '../../../NonReact/utils/CxrSubscriptionAccessUtils'
import { CX_SCORE_LICENCE_I18N_PREFIX } from '../../../NonReact/utils/CxsSubscriptionAccessUtils'
import { ReportingAccessLevels } from '../Reporting/ReportingAccessLevels'

/**
 * Whether a device/agent form value identifies a specific, concrete selection rather than being empty or
 * one of the All / None / Unknown sentinels.
 *
 * @param value - Endpoint or scope device/agent form value.
 * @returns True when the value is a specific device/agent.
 */
const isSpecificLink = (value: Nullable<string>): boolean =>
    isStringNotEmpty(value) &&
    value !== SERVER_NONE_VALUE &&
    value !== SERVER_ALL_VALUE &&
    value !== SERVER_UNKNOWN_VALUE

/** A react-hook-form field validator: returns an error message, or undefined when the value is acceptable. */
export type EndpointFieldValidator = (value: string) => ValidateResult

/** Inputs for {@link licenceEndpointDeviceRequiredRule}. */
export type LicenceEndpointDeviceRuleOptions = {
    /** Language handler for localized error messages. */
    language: CLanguageHandler
    /** react-hook-form watch used to read dependent field values. */
    watch: UseFormMethods['watch']
    /** Which endpoint field this instance validates. */
    field: EndpointFieldKind
    /** Whether the CXS subscription UI and rules currently apply. */
    isCxsSubscriptionApplicable: boolean
    /** Whether the CXR subscription UI and rules currently apply. */
    isCxrSubscriptionApplicable: boolean
}

/**
 * Validation rule enforcing that a CXS Agent or CXR User licence is linked to a specific device/agent.
 *
 * The link may be satisfied by the extension endpoint (device/agent) or, where the reporting level allows it,
 * by the user's scope (Permissions tab device/agent); a specific scope selection silently clears the endpoint
 * requirement.
 *
 * - CXS Agent: satisfied only by a specific endpoint agent or scope agent — a device never resolves to an agent
 *   on the backend, so it would be rejected on save (error 11414). Enforced for every reporting level except
 *   Essentials while the CXS subscription UI is applicable, and reported on the endpoint agent field alone,
 *   because the endpoint device field is disabled for this subscription (APD-13455).
 * - CXR User: enforced only for the CXA None and Manager reporting levels, satisfied by a specific endpoint
 *   device or agent (the backend resolves an agent to its matched device), and reported on both endpoint fields
 *   because either clears it. Scope differs by level: CXA None may also be satisfied by a scope device, while a
 *   Manager must keep partition/telsys scope, so a scoped device would be rejected on save (error 11227).
 *
 * @param options - Language handler, form watch, bound field, and CXS / CXR applicability (see
 *     {@link LicenceEndpointDeviceRuleOptions}).
 * @returns A validation function returning an error message when the required link is missing, else undefined.
 */
export const licenceEndpointDeviceRequiredRule =
    ({
        language,
        watch,
        field,
        isCxsSubscriptionApplicable,
        isCxrSubscriptionApplicable
    }: LicenceEndpointDeviceRuleOptions): EndpointFieldValidator =>
    (value: string): ValidateResult => {
        const reportingLevelValue: Nullable<string> = watch(REPORTING_LEVEL_NAME)

        if (reportingLevelValue === ReportingAccessLevels.ESSENTIALS) return undefined

        const isAgentField: boolean = field === ENDPOINT_FIELD_KIND.AGENT
        const endpointDevice: Nullable<string> = isAgentField ? watch(ENDPOINT_DEVICE_NAME) : value
        const endpointAgent: Nullable<string> = isAgentField ? value : watch(ENDPOINT_AGENT_NAME)
        const scopeDevice: Nullable<string> = watch(DEVICES_PERM_NAME)
        const scopeAgent: Nullable<string> = watch(AGENTS_PERM_NAME)

        const cxsSubscriptionValue: Nullable<string> = watch(CXS_SUBSCRIPTION_NAME)

        if (isAgentField && isCxsSubscriptionApplicable && cxsSubscriptionValue === CXScoreLicences.CXS_AGENT) {
            // Only an agent satisfies CXS Agent: the backend resolves one from the endpoint agent or the scope
            // agent, and maps an agent to its device but never a device to an agent, so a device — endpoint or
            // scope — is rejected on save (error 11414). The endpoint device field is disabled for this
            // subscription, so the requirement is reported here rather than on a field that cannot clear it.
            const isLinked: boolean = isSpecificLink(endpointAgent) || isSpecificLink(scopeAgent)

            if (!isLinked) {
                return language.getInterpolated(
                    CXScoreLanguage.CX_Score_Agent_Error,
                    language.get(`${CX_SCORE_LICENCE_I18N_PREFIX}${cxsSubscriptionValue}`)
                )
            }
        }

        const cxrSubscriptionValue: Nullable<string> = watch(CXR_SUBSCRIPTION_NAME)

        const isManagerLevel: boolean = reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR

        const isNoneOrManagerLevel: boolean = reportingLevelValue === ReportingAccessLevels.NONE || isManagerLevel

        if (isCxrSubscriptionApplicable && cxrSubscriptionValue === CXRecordLicences.CXR_USER && isNoneOrManagerLevel) {
            // Either endpoint field satisfies CXR User, because the backend resolves an endpoint agent to its
            // matched device (matrix rows R22/R28, Endpoint "Device / Agent"), so the requirement is reported on
            // both. Scope is where the levels differ: a Manager must keep partition/telsys scope and the backend
            // rejects a scoped device outright (error 11227), while CXA None may satisfy it by scope device.
            const isLinked: boolean =
                isSpecificLink(endpointDevice) ||
                isSpecificLink(endpointAgent) ||
                (!isManagerLevel && isSpecificLink(scopeDevice))

            if (!isLinked) {
                return language.getInterpolated(
                    CXRecordLanguage.CX_Record_Device_Or_Agent_Error,
                    language.get(`${CX_RECORD_LICENCE_I18N_PREFIX}${cxrSubscriptionValue}`)
                )
            }
        }

        return undefined
    }
